import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { useEffect, lazy, Suspense, type ComponentType } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

// Components
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { CookieConsent } from "@/components/CookieConsent";

// Pages
import Home from "@/pages/Home";
// The homepage is eager; other pages load only when visited in the browser.
// The server entry supplies eager components so every route still prerenders.
const clientPages = {
  Home,
  Services: lazy(() => import("@/pages/Services")),
  ServicePage: lazy(() => import("@/pages/ServicePage")),
  ServiceCityPage: lazy(() => import("@/pages/ServiceCityPage")),
  Contact: lazy(() => import("@/pages/Contact")),
  About: lazy(() => import("@/pages/About")),
  TermesConditions: lazy(() => import("@/pages/TermesConditions")),
  VilleLongueuil: lazy(() => import("@/pages/VilleLongueuil")),
  VilleStHubert: lazy(() => import("@/pages/VilleStHubert")),
  VilleGreenfieldPark: lazy(() => import("@/pages/VilleGreenfieldPark")),
  VilleStLambert: lazy(() => import("@/pages/VilleStLambert")),
  Zones: lazy(() => import("@/pages/Zones")),
  FiltrationEau: lazy(() => import("@/pages/FiltrationEau")),
  PolitiqueConfidentialite: lazy(() => import("@/pages/PolitiqueConfidentialite")),
  PlombierRiveSud: lazy(() => import("@/pages/PlombierRiveSud")),
  NotFound: lazy(() => import("@/pages/not-found")),
};
export type PageComponents = Record<keyof typeof clientPages, ComponentType>;

const queryClient = new QueryClient();

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location]);
  return null;
}

// Redirect component for old URLs
function Router({ pages }: { pages: PageComponents }) {
  const { Home, Services, ServicePage, ServiceCityPage, Contact, About, TermesConditions,
    VilleLongueuil, VilleStHubert, VilleGreenfieldPark, VilleStLambert, Zones,
    FiltrationEau, PolitiqueConfidentialite, PlombierRiveSud, NotFound } = pages;
  return (
    <>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/services" component={Services} />
        <Route path="/services/filtration-eau/" component={FiltrationEau} />
        <Route path="/services/:service" component={ServicePage} />
        <Route path="/services/:service/:city" component={ServiceCityPage} />

        {/* Old URL redirects - keep for backward compat */}
        <Route path="/urgence" component={() => <Redirect to="/services/urgence/" />} />
        <Route path="/debouchage" component={() => <Redirect to="/services/debouchage/" />} />
        <Route path="/chauffe-eau" component={() => <Redirect to="/services/chauffe-eau/" />} />
        <Route path="/egouts-drains" component={() => <Redirect to="/services/egouts-drains/" />} />
        <Route path="/detection-fuites" component={() => <Redirect to="/services/detection-fuites/" />} />
        <Route path="/reparation-tuyaux" component={() => <Redirect to="/services/reparation-tuyaux/" />} />
        <Route path="/inspection-camera" component={() => <Redirect to="/services/inspection-camera/" />} />
        <Route path="/hydro-jet" component={() => <Redirect to="/services/hydro-jet/" />} />
        <Route path="/pompe-puisard" component={() => <Redirect to="/services/pompe-puisard/" />} />
        <Route path="/clapet-antiretour" component={() => <Redirect to="/services/clapet-antiretour/" />} />
        <Route path="/toilettes-accessoires" component={() => <Redirect to="/services/toilettes-accessoires/" />} />
        <Route path="/tuyaux-geles" component={() => <Redirect to="/services/tuyaux-geles/" />} />

        <Route path="/plombier-longueuil" component={() => <Redirect to="/plombier-vieux-longueuil/" />} />
        <Route path="/plombier-vieux-longueuil" component={VilleLongueuil} />
        <Route path="/plombier-saint-hubert" component={VilleStHubert} />
        <Route path="/plombier-greenfield-park" component={VilleGreenfieldPark} />
        <Route path="/plombier-saint-lambert" component={VilleStLambert} />
        <Route path="/plombier-rive-sud" component={PlombierRiveSud} />
        <Route path="/zones" component={Zones} />
        <Route path="/contact" component={Contact} />
        <Route path="/a-propos" component={About} />
        <Route path="/termes-et-conditions" component={TermesConditions} />
        <Route path="/politique-de-confidentialite" component={PolitiqueConfidentialite} />
        <Route component={NotFound} />
      </Switch>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function App({ ssrHook, ssrPages }: { ssrHook?: any; ssrPages?: PageComponents } = {}) {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")} hook={ssrHook}>
          <div className="min-h-[100dvh] flex flex-col font-sans">
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<div role="status" className="px-4 py-32 text-center text-primary">Chargement de la page…</div>}>
                <Router pages={ssrPages ?? clientPages} />
              </Suspense>
            </main>
            <Footer />
            <CookieConsent />
          </div>
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
